import {useCallback, useEffect, useState} from 'react'
import {useSearchParams, useNavigate} from 'react-router-dom'
import {useIntl} from 'react-intl'
import SharedData from '../../common/SharedData'
import Loader from '../../common/Loader'
import Paginator from '../../common/Paginator'
import {getBulkJobs, downloadBulkJobResult, IBulkJob} from './core/_requests'

/**
 * Supplier "Bulk Jobs" tracking page. Lists the supplier's async bulk-sheet
 * jobs (price/qty uploads) with live status + a result download. Auto-refreshes
 * while any job is still queued/processing.
 */
const PAGE_SIZE = 20

const STATUSES = [
  'queued',
  'processing',
  'completed',
  'completed_with_errors',
  'failed',
] as const

const statusBadge = (s: string): string => {
  switch (s) {
    case 'completed':
      return 'badge badge-light-success'
    case 'completed_with_errors':
      return 'badge badge-light-warning'
    case 'failed':
      return 'badge badge-light-danger'
    case 'processing':
      return 'badge badge-light-info'
    default:
      return 'badge badge-light'
  }
}

const BulkJobsList = () => {
  const {currentUser, language} = SharedData()
  const {formatMessage} = useIntl()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [jobs, setJobs] = useState<IBulkJob[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)

  const page = Math.max(1, parseInt(searchParams.get('current_page') ?? '1', 10) || 1)
  const status = searchParams.get('status') ?? ''

  // Map backend enums → human labels (fall back to the raw enum if unmapped).
  const statusLabel = (s: string): string => {
    const known = (STATUSES as readonly string[]).includes(s)
    return known ? formatMessage({id: `bulk_jobs.status.${s}`}) : s
  }
  const operationLabel = (op: string): string => {
    const known = ['hisbah_product_bulk', 'hisbah_special_bulk', 'hisbah_offer_bulk', 'hisbah_images_zip'].includes(op)
    return known ? formatMessage({id: `bulk_jobs.operation.${op}`}) : op
  }

  const fetchJobs = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getBulkJobs(
        {page, limit: PAGE_SIZE, status: status || undefined},
        currentUser?.token,
        language,
      )
      setJobs(res.data?.data ?? [])
      setTotal(res.data?.total ?? 0)
    } catch {
      setJobs([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [page, status, currentUser?.token, language])

  useEffect(() => {
    void fetchJobs()
  }, [fetchJobs])

  // Live progress: re-poll while anything is still running.
  useEffect(() => {
    const active = jobs.some((j) => j.status === 'queued' || j.status === 'processing')
    if (!active) return
    const t = setTimeout(() => {
      void fetchJobs()
    }, 5000)
    return () => clearTimeout(t)
  }, [jobs, fetchJobs])

  const onFilter = (s: string) => {
    const sp = new URLSearchParams(searchParams)
    if (s) sp.set('status', s)
    else sp.delete('status')
    sp.set('current_page', '1')
    setSearchParams(sp)
  }

  return (
    <div className='card'>
      <div className='card-header align-items-center'>
        <h3 className='card-title'>{formatMessage({id: 'bulk_jobs.title'})}</h3>
        <div className='card-toolbar'>
          <select
            className='form-select form-select-sm w-200px'
            value={status}
            onChange={(e) => onFilter(e.target.value)}
          >
            <option value=''>{formatMessage({id: 'bulk_jobs.filter.all'})}</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {formatMessage({id: `bulk_jobs.status.${s}`})}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className='card-body position-relative'>
        {loading ? <Loader isFull /> : null}
        <div className='table-responsive'>
          <table className='table table-row-bordered align-middle gy-3'>
            <thead>
              <tr className='fw-bold text-muted'>
                <th>{formatMessage({id: 'bulk_jobs.col_number'})}</th>
                <th>{formatMessage({id: 'bulk_jobs.col_file'})}</th>
                <th>{formatMessage({id: 'bulk_jobs.col_status'})}</th>
                <th>{formatMessage({id: 'bulk_jobs.col_progress'})}</th>
                <th>{formatMessage({id: 'bulk_jobs.col_queued'})}</th>
                <th>{formatMessage({id: 'bulk_jobs.col_finished'})}</th>
                <th className='text-end'>{formatMessage({id: 'bulk_jobs.col_result'})}</th>
              </tr>
            </thead>
            <tbody>
              {jobs.length === 0 && !loading ? (
                <tr>
                  <td colSpan={7} className='text-center text-muted'>
                    {formatMessage({id: 'bulk_jobs.empty'})}
                  </td>
                </tr>
              ) : null}
              {jobs.map((j) => (
                <tr key={j.job_id}>
                  <td>{j.job_id}</td>
                  <td>
                    {j.original_filename}
                    {j.operation_type ? (
                      <div className='text-muted fs-7'>{operationLabel(j.operation_type)}</div>
                    ) : null}
                  </td>
                  <td>
                    <span className={statusBadge(j.status)}>{statusLabel(j.status)}</span>
                  </td>
                  <td>
                    {j.processed_rows} / {j.total_rows}
                    {j.failed_rows > 0 ? (
                      <span className='text-danger'>
                        {' '}
                        {formatMessage({id: 'bulk_jobs.failed_rows'}, {count: j.failed_rows})}
                      </span>
                    ) : null}
                  </td>
                  <td>{j.queued_at ?? ''}</td>
                  <td>{j.finished_at ?? ''}</td>
                  <td className='text-end'>
                    {j.review_required ? (
                      <button
                        className='btn btn-sm btn-light-warning me-2'
                        onClick={() => navigate(`/products/bulk-jobs/${j.job_id}/review`)}
                      >
                        {formatMessage(
                          {id: 'bulk_jobs.resolve_variants'},
                          {count: j.pending_count ? `${j.pending_count} ` : ''},
                        )}
                      </button>
                    ) : null}
                    {j.has_result ? (
                      <button
                        className='btn btn-sm btn-light-success'
                        onClick={() => downloadBulkJobResult(j, currentUser?.token, language)}
                      >
                        {formatMessage({id: 'bulk_jobs.download'})}
                      </button>
                    ) : !j.review_required ? (
                      <span className='text-muted'>—</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Paginator totalResult={total} perPage={PAGE_SIZE} />
      </div>
    </div>
  )
}

export default BulkJobsList
